import { State, Category, Cabin1Class, Insurance } from './vuex'

import { filter, find, findIndex, defaultTo, get, isNil, sum } from 'lodash'

const insuranceIDToName: { [id: string]: string } = {
  'RRS': 'Reise-Rücktritt',
  'KS': 'AIDA-Komplettschutz',
}

function _insuranceFromCabin(state: Insurance) {
  for (const insurance of state.list) {
    if (insurance.code === state.selected) return insurance
  }
  return null
}

function _getInsurance(state: Cabin1Class) {
  return _insuranceFromCabin(state['insurance'])
}

export function getPremiumCabinDetail(cabin: Cabin1Class) {
  const selectionConfig = get(
    cabin,
    'cabins.selectionCategoryConfigurations',
    false,
  )

  if (selectionConfig) {
    const result = filter(selectionConfig, item => {
      return item.cabin !== null && item.subCatgory !== null
    })

    return result[0]
  }
}

function getCabinCategory(cabin: Cabin1Class): Category | undefined {
  const cc = get(cabin, 'cabins.cabinCategories.0', 'n/a')
  if (cc === undefined) {
    return undefined
  }

  const mainCabinCategoryID = defaultTo(
    get(cabin, 'cabins.cabinCategory'),
    cc.mainCode,
  )

  const mainCabinCategoryIndex = findIndex(
    get(cabin, 'cabins.cabinCategories', []),
    ['mainCode', mainCabinCategoryID],
  )

  if (mainCabinCategoryIndex === -1) {
    return undefined
  }

  const mainCabinCategory = get(
    cabin,
    `cabins.cabinCategories.${mainCabinCategoryIndex}`,
  )

  if (isNil(mainCabinCategory)) {
    return undefined
  }

  const activeAdditionalCategory = get(
    cabin,
    'cabins.activeAdditionalCategory',
    'NONE',
  )

  if (
    activeAdditionalCategory === 'PREMIUM' ||
    activeAdditionalCategory === 'PREMIUMAI'
  ) {
    let selectionConfig = get(
      cabin,
      `cabins.selectionCategoryConfigurations.${mainCabinCategoryIndex}`,
    )
    if (isNil(selectionConfig) || isNil(selectionConfig.subCategory)) {
      // Fallback on a different approach if selectionConfig index does not match cabin index
      const c = get(cabin, `cabins.cabins.${mainCabinCategoryIndex}`)
      if (isNil(c)) {
        return mainCabinCategory
      }
      const selectionCategoryConfigurations = get(
        cabin,
        'cabins.selectionCategoryConfigurations',
        [],
      )
      selectionConfig = find(selectionCategoryConfigurations, [
        'subCategory',
        c.category,
      ])!
      if (isNil(selectionConfig)) {
        return undefined
      }
    }

    const mainCabinCategories =
      activeAdditionalCategory === 'PREMIUMAI'
        ? mainCabinCategory.premiumAICategories
        : mainCabinCategory.premiumCategories

    return find(mainCabinCategories, ['code', selectionConfig.subCategory])
  }
  return mainCabinCategory
}

export function getCruiseProduct(
  state: State,
  cabinName: string,
  step?: number,
): { [key: string]: string | number } {
  let cabin: Cabin1Class = get(state, `booking.${cabinName}`, {
    persons: {
      adults: 0,
      juveniles: 0,
      children: 0,
    },
  })

  let cabinCategory = defaultTo(getCabinCategory(cabin), {
    name: '',
    price: 0,
    pricingsPerPax: [],
    surcharges: [],
    amenities: [],
    premiumAvailable: false,
    premiumAIAvailable: false,
    cheapestPremiumPrice: 0,
    cheapestPremiumAIPrice: 0,
  })
  let price =
    get(cabinCategory, 'price', 0) === 0
      ? sum(cabinCategory.pricingsPerPax)
      : get(cabinCategory, 'price', 0)

  let priceModel =
    get(state, 'booking.general.cruise.priceModel', 'n/a') != 'IND'
      ? get(state, 'booking.general.cruise.priceModel', 'n/a')
      : 'PREMIUM'

  if (
    (cabinCategory.premiumAvailable && priceModel === 'PREMIUM') ||
    (cabinCategory.premiumAIAvailable && priceModel === 'PREMIUMAI')
  ) {
    price =
      priceModel === 'PREMIUM'
        ? cabinCategory.cheapestPremiumPrice || 0
        : cabinCategory.cheapestPremiumAIPrice || 0
  }
  return {
    name: step && step >= 2 ? handleCabinName(cabinCategory) : 'Kabine',
    type: 'cabin',
    price: step && step >= 2 ? price.toFixed(2) : '0.00',
  }
}

function handleCabinName(cabinCategory: any) {
  let displayName = cabinCategory.displayName
  if (displayName != 'Suite') {
    return get(cabinCategory, 'name', 'n/a')
  }
  let fullName: string = cabinCategory.head
  fullName = fullName.replace('Verandakabine Deluxe', 'Verandakabine-Deluxe')
  fullName = fullName.replace('Verandakabine Komfort', 'Verandakabine-Komfort')
  return fullName.split(' ').shift()
}

export function getTransportationProducts(
  state: State,
  cabinName: string,
): { [key: string]: string | number | any }[] {
  let bookings: any = get(state, 'booking')
  let cabin: Cabin1Class = bookings[cabinName]
  if (cabin === undefined) {
    return []
  }
  let products: any = []
  let transportationType = get(cabin, 'transport.type', 'n/a')
  // Transportations
  let airportID = get(cabin, 'transportAir.outgoingSelection.selected')
  let classID = get(cabin, 'transportAir.outgoingSelection.flightClass')
  let twoWay = get(cabin, 'transportAir.flights.twoWay', {})
  //let rnfTotalPrice = get(cabin, 'transportAir.rnfTotalPrice', 0.0)
  let flight: any = get(twoWay, `${classID}.${airportID}[0]`, {})
  switch (transportationType) {
    case 'flight':
      let outgoingAirportID = get(
        cabin,
        'transportAir.outgoingSelection.selected',
      )
      let outgoingClassID = get(
        cabin,
        'transportAir.outgoingSelection.flightClass',
      )
      let outgoingFlight: any = get(
        cabin,
        `transportAir.flights.outgoing.${outgoingClassID}.${outgoingAirportID}[0]`,
        get(
          cabin,
          `transportAir.flights.twoWay.${outgoingClassID}.${outgoingAirportID}[0]`,
          {},
        ),
      )
      let returnAirportID = get(
        cabin,
        'transportAir.returningSelection.selected',
      )
      let returnClassID = defaultTo(
        get(cabin, 'transportAir.returningSelection.flightClass'),

        get(cabin, 'transportAir.outgoingSelection.flightClass'),
      )
      let returnFlight: any = get(
        cabin,
        `transportAir.flights.returning.${returnClassID}.${returnAirportID}[0]`,
        get(
          cabin,
          `transportAir.flights.twoWay.${returnClassID}.${returnAirportID}[0]`,
          {},
        ),
      )
      let outGoingPrice: number = get(
        outgoingFlight,
        'outgoingFlight.price',
        0.0,
      )
      let returnPrice: number = get(returnFlight, 'returnFlight.price', 0.0)
      let returnFlights = get(returnFlight, 'returnFlight', 0.0)
      let priceSum: any = (outGoingPrice + returnPrice).toFixed(2)
      let outgoingFlights = get(outgoingFlight, 'outgoingFlight', 0.0)
      if (
        get(cabin, 'transportAir.withOutNoFlight', false) === false &&
        get(cabin, 'transportAir.withRetNoFlight', false) === false &&
        get(cabin, 'transportAir.outgoingSelection.activated', false) &&
        get(cabin, 'transportAir.returningSelection.activated', false) &&
        get(cabin, 'transportAir.outgoingSelection.selected', false) ===
          get(cabin, 'transportAir.returningSelection.selected', false)
      ) {
        buildProductAndPush(
          products,
          outGoingPrice,
          returnPrice,
          true,
          true,
          get(flight, 'outgoingFlight.segments[0].carrier.name', 'n/a'),
          get(flight, 'returnFlight.segments[0].carrier.name', 'n/a'),
          get(flight, 'className', 'n/a'),
          get(flight, 'className', 'n/a'),
          get(flight, 'outgoingFlight.segments[0].departureAirportCode', 'n/a'),
          get(flight, 'outgoingFlight.segments[0].arrivalAirportCode', 'n/a'),
          true,
          priceSum,
        )
        break
      }
      if (
        get(cabin, 'transportAir.outgoingSelection.activated', false) &&
        get(cabin, 'transportAir.returningSelection.activated', false)
      ) {
        if (outgoingFlights && returnFlights) {
          //outgoing flight (handle multiple flights)
          for (let i = 0; i < outgoingFlights.segments.length; i++) {
            buildOutGoingFlights(
              outgoingFlight,
              products,
              outGoingPrice,
              returnPrice,
              priceSum,
              i,
            )
          }
          //return flight (handle multiple flights)
          for (let i = 0; i < returnFlights.segments.length; i++) {
            buildReturnFlights(
              returnFlight,
              products,
              outGoingPrice,
              returnPrice,
              priceSum,
              i,
            )
          }
        } else if (outgoingFlights && !returnFlights) {
          //outgoing flight (handle multiple flights)
          for (let i = 0; i < outgoingFlights.segments.length; i++) {
            buildOutGoingFlights(
              outgoingFlight,
              products,
              outGoingPrice,
              returnPrice,
              priceSum,
              i,
            )
          }
          products.push({
            name: 'Individuelle Abreise',
            type: 'transfer',
            price: '0.00',
            transfer: {
              direction: 'return',
              type: 'individual',
              price: priceSum,
            },
          })
        } else if (!outgoingFlights && returnFlights) {
          //return flight (handle multiple flights)
          products.push({
            name: 'Individuelle Anreise',
            type: 'transfer',
            price: '0.00',
            transfer: {
              direction: 'outbound',
              type: 'individual',
              price: priceSum,
            },
          })
          for (let i = 0; i < returnFlights.segments.length; i++) {
            buildReturnFlights(
              returnFlight,
              products,
              outGoingPrice,
              returnPrice,
              priceSum,
              i,
            )
          }
        }
      } else if (
        get(cabin, 'transportAir.outgoingSelection.activated', false) &&
        !get(cabin, 'transportAir.returningSelection.activated', false)
      ) {
        //outgoing flight (handle multiple flights)
        if (outgoingFlights) {
          for (let i = 0; i < outgoingFlights.segments.length; i++) {
            buildOutGoingFlights(
              outgoingFlight,
              products,
              outGoingPrice,
              returnPrice,
              priceSum,
              i,
            )
          }
          products.push({
            name: 'Individuelle Abreise',
            type: 'transfer',
            price: '0.00',
            transfer: {
              direction: 'return',
              type: 'individual',
              price: priceSum,
            },
          })
        }
      } else if (
        !get(cabin, 'transportAir.outgoingSelection.activated', false) &&
        get(cabin, 'transportAir.returningSelection.activated', false)
      ) {
        //return flight (handle multiple flights)
        products.push({
          name: 'Individuelle Anreise',
          type: 'transfer',
          price: '0.00',
          transfer: {
            direction: 'outbound',
            type: 'individual',
            price: priceSum,
          },
        })
        if (returnFlights) {
          for (let i = 0; i < returnFlights.segments.length; i++) {
            buildReturnFlights(
              returnFlight,
              products,
              outGoingPrice,
              returnPrice,
              priceSum,
              i,
            )
          }
        }
      }
      break
    case 'rail':
      let hasDeparture: boolean = get(cabin, 'transportRail.departure', false)
      let hasArrival: boolean = get(cabin, 'transportRail.arrival', false)
      let twoWay: boolean = hasDeparture && hasArrival
      let totalPrice: number = get(cabin, 'transportRail.totalPrice', 0.0)
      let price = twoWay ? totalPrice / 2 : totalPrice
      buildProductAndPush(
        products,
        price,
        price,
        hasDeparture,
        hasArrival,
        'Deutsche Bahn',
        'Deutsche Bahn',
        get(cabin, 'className', 'n/a'),
        get(cabin, 'className', 'n/a'),
        get(cabin, 'transportRail.departureConfig.location', 'n/a'),
        get(cabin, 'transportRail.arrivalConfig.location', 'n/a'),
        false,
        totalPrice,
      )
      break
    default:
      products.push(
        {
          name: 'Individuelle Anreise',
          type: 'transfer',
          price: '0.00',
          transfer: {
            direction: 'outbound',
            type: 'individual',
          },
        },
        {
          name: 'Individuelle Abreise',
          type: 'transfer',
          price: '0.00',
          transfer: {
            direction: 'return',
            type: 'individual',
          },
        },
      )
  }
  return products
}

export function getInsuranceProducts(
  state: State,
  cabinName: string,
): { [key: string]: string | number }[] {
  const bookings: any = get(state, 'booking')
  const cabin: Cabin1Class = bookings[cabinName]
  if (cabin === undefined) {
    return []
  }
  const insurance = _getInsurance(cabin)
  if (insurance) {
    const insurancePackage: string | null = get(insurance, 'package', null)
    const insuranceID: string = insurancePackage ? get(insuranceIDToName, insurancePackage, 'n/a') : 'n/a'
    return [
      {
        name: insuranceID,
        type: 'insurance',
        price: get(insurance, 'price', 0.0).toFixed(2),
      },
    ]
  }
  return [
    {
      name: 'Keine Versicherung',
      type: 'insurance',
      price: '0.00',
    },
  ]
}

// locale Helpers for handling flights (outgoing and return are possible multiple )
function buildOutGoingFlights(
  outgoingFlight: any,
  products: any,
  outGoingPrice: any,
  returnPrice: any,
  priceSum: number,
  index: number = 0,
) {
  buildProductAndPush(
    products,
    index === 0 ? outGoingPrice : 0.0,
    index === 0 ? returnPrice : 0.0,
    true,
    false,
    get(
      outgoingFlight,
      `outgoingFlight.segments[${index}].carrier.name`,
      'n/a',
    ),
    get(outgoingFlight, `returnFlight.segments[${index}].carrier.name`, 'n/a'),
    get(outgoingFlight, 'className', 'n/a'),
    get(outgoingFlight, 'className', 'n/a'),
    get(
      outgoingFlight,
      `outgoingFlight.segments[${index}].departureAirportCode`,
      'n/a',
    ),
    get(
      outgoingFlight,
      `outgoingFlight.segments[${index}].arrivalAirportCode`,
      'n/a',
    ),
    true,
    priceSum,
  )
}

function buildReturnFlights(
  returnFlight: any,
  products: any,
  outGoingPrice: any,
  returnPrice: any,
  priceSum: number,
  index: number = 0,
) {
  buildProductAndPush(
    products,
    index === 0 ? outGoingPrice : 0.0,
    index === 0 ? returnPrice : 0.0,
    false,
    true,
    get(returnFlight, `outgoingFlight.segments[${index}].carrier.name`, 'n/a'),
    get(returnFlight, `returnFlight.segments[${index}].carrier.name`, 'n/a'),
    get(returnFlight, 'className', 'n/a'),
    get(returnFlight, 'className', 'n/a'),
    get(
      returnFlight,
      `returnFlight.segments[${index}].departureAirportCode`,
      'n/a',
    ),
    get(
      returnFlight,
      `returnFlight.segments[${index}].arrivalAirportCode`,
      'n/a',
    ),
    true,
    priceSum,
  )
}

function buildProductAndPush(
  products: any[],
  priceDeparture: number,
  priceArrival: number,
  hasDeparture: boolean,
  hasArrival: boolean,
  brandDeparture: string,
  brandArrival: string,
  classDeparture: string,
  classArrival: string,
  pointOfDeparture: string,
  pointOfArrival: string,
  isFly: boolean,
  totalPrice: number,
) {
  let tempProduct: any = []
  if (hasDeparture) {
    tempProduct = {
      name: isFly ? 'Fluganreise' : 'Zuganreise',
      type: 'transfer',
      price: priceDeparture.toFixed(2),
      transfer: {
        direction: 'outbound',
        type: isFly ? 'plane' : 'train',
        pointOfDeparture: pointOfDeparture,
        pointOfArrival: pointOfArrival,
        price: `${totalPrice}`,
      },
    }
    if (classDeparture) {
      tempProduct.transfer.class = classDeparture
    }
    if (brandDeparture) {
      tempProduct.transfer.brand = brandDeparture
    }
    products.push(tempProduct)
  }
  if (hasArrival) {
    tempProduct = {
      name: isFly ? 'Flugabreise' : 'Zugabreise',
      type: 'transfer',
      price: priceArrival.toFixed(2),
      transfer: {
        direction: 'return',
        type: isFly ? 'plane' : 'train',
        pointOfDeparture: pointOfDeparture,
        pointOfArrival: pointOfArrival,
        price: `${totalPrice}`,
      },
    }

    if (classArrival) {
      tempProduct.transfer.class = classArrival
    }
    if (brandArrival) {
      tempProduct.transfer.brand = brandArrival
    }
    products.push(tempProduct)
  }
}